import { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  Button,
  Spin,
  App,
  Space,
  Form,
  DatePicker,
  Statistic,
} from 'antd';
import {
  FileExcelOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { listAllStates, listCompetitionRegions } from '../../services/catalogs';
import { getRegionById } from '../../services/regions';
import { getSalesReport } from '../../services/reports';
import { exportToExcel } from '../../utils/excel';

const { RangePicker } = DatePicker;

const PERIODS = [
  { value: 'month', label: 'Mes' },
  { value: '60d', label: '60 días' },
  { value: '90d', label: '90 días' },
  { value: 'custom', label: 'Personalizado' },
];

const MEDIUM_LABELS = {
  apple_iap: 'Apple',
  stripe: 'Stripe',
  google: 'Google',
  shop: 'Tienda',
  sin_pago: 'Sin referencia de pago',
};

const EMPTY_SUMMARY = {
  membershipCount: 0,
  membershipAmount: 0,
  addonCount: 0,
  addonAmount: 0,
  totalCount: 0,
  totalAmount: 0,
};

const formatAmount = (amount, currency) => {
  const formatted = new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount ?? 0);
  return currency ? `${formatted} ${currency}` : formatted;
};

const formatDate = (value) =>
  value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '';

const mediumLabel = (value) => MEDIUM_LABELS[value] || value || '';

const addonDetail = (item) => {
  if (item.productType !== 'Videos extra') return '';
  const videos =
    item.addonVideos == null ? '' : `${item.addonVideos} videos`;
  return [item.addonName, videos].filter(Boolean).join(' · ');
};

const summaryCurrency = (items) => {
  const currencies = [...new Set((items || []).map((item) => item.currency).filter(Boolean))];
  return currencies.length === 1 ? currencies[0] : '';
};

const SalesReport = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const period = Form.useWatch('period', form);
  const regionId = Form.useWatch('regionId', form);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [regions, setRegions] = useState([]);
  const [states, setStates] = useState([]);
  const [statesInRegion, setStatesInRegion] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);

  useEffect(() => {
    Promise.all([listCompetitionRegions(), listAllStates()]).then(([regionList, stateList]) => {
      setRegions(regionList || []);
      setStates(stateList || []);
    });
  }, []);

  useEffect(() => {
    if (!regionId) {
      setStatesInRegion([]);
      return;
    }

    let cancelled = false;
    setLoadingStates(true);
    getRegionById(regionId)
      .then((region) => {
        if (cancelled) return;
        const list = (region?.regionStates ?? [])
          .map((item) => item?.state)
          .filter(Boolean);
        setStatesInRegion(list);
        const currentStateId = form.getFieldValue('stateId');
        if (
          currentStateId != null &&
          !list.some((state) => state.id === currentStateId)
        ) {
          form.setFieldValue('stateId', undefined);
        }
      })
      .catch(() => {
        if (!cancelled) {
          message.error('No se pudieron cargar los estados de la región');
          setStatesInRegion([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingStates(false);
      });

    return () => {
      cancelled = true;
    };
  }, [regionId, form, message]);

  const buildParams = (values) => {
    const params = { period: values.period || 'month' };
    if (values.regionId != null) params.regionId = values.regionId;
    if (values.stateId != null) params.stateId = values.stateId;
    if (params.period === 'custom') {
      const range = values.dateRange;
      if (!range?.[0] || !range?.[1]) return null;
      params.dateFrom = range[0].format('YYYY-MM-DD');
      params.dateTo = range[1].format('YYYY-MM-DD');
    }
    return params;
  };

  const handleSearch = async (values = form.getFieldsValue()) => {
    const params = buildParams(values);
    if (!params) {
      message.warning('Selecciona el rango de fechas.');
      return;
    }

    setLoading(true);
    try {
      const res = await getSalesReport(params);
      if (res?.ok && res?.data) {
        setData(res.data.items || []);
        setSummary(res.data.summary || EMPTY_SUMMARY);
      } else {
        setData([]);
        setSummary(EMPTY_SUMMARY);
      }
    } catch (err) {
      message.error(err?.__normalized?.message || 'Error al obtener el reporte.');
      setData([]);
      setSummary(EMPTY_SUMMARY);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    const values = {
      period: 'month',
      regionId: undefined,
      stateId: undefined,
      dateRange: undefined,
    };
    form.setFieldsValue(values);
    setStatesInRegion([]);
    handleSearch(values);
  };

  const handleExportExcel = () => {
    if (!data.length) {
      message.warning('No hay datos para exportar. Ejecute primero la búsqueda.');
      return;
    }

    setExporting(true);
    try {
      const currency = summaryCurrency(data);
      const excelData = data.map((item) => ({
        Fecha: formatDate(item.purchasedAt),
        Usuario: item.userName || '',
        Correo: item.userEmail || '',
        'Tipo de producto': item.productType || '',
        'Tipo de membresía': item.membershipType || '',
        'Videos extra': addonDetail(item),
        Monto: item.amount ?? 0,
        Moneda: item.currency || '',
        'Medio de pago': mediumLabel(item.paymentMedium),
        Referencia: item.paymentReference || '',
        Región: item.region || '',
        Estado: item.state || '',
      }));

      excelData.push({
        Fecha: '',
        Usuario: '',
        Correo: '',
        'Tipo de producto': '',
        'Tipo de membresía': '',
        'Videos extra': '',
        Monto: '',
        Moneda: '',
        'Medio de pago': '',
        Referencia: '',
        Región: '',
        Estado: '',
      });
      excelData.push({
        Fecha: 'Resumen de ingresos',
        Usuario: 'Membresías',
        Correo: '',
        'Tipo de producto': `${summary.membershipCount} ventas`,
        'Tipo de membresía': '',
        'Videos extra': '',
        Monto: summary.membershipAmount,
        Moneda: currency,
        'Medio de pago': '',
        Referencia: '',
        Región: '',
        Estado: '',
      });
      excelData.push({
        Fecha: 'Resumen de ingresos',
        Usuario: 'Videos extra',
        Correo: '',
        'Tipo de producto': `${summary.addonCount} ventas`,
        'Tipo de membresía': '',
        'Videos extra': '',
        Monto: summary.addonAmount,
        Moneda: currency,
        'Medio de pago': '',
        Referencia: '',
        Región: '',
        Estado: '',
      });
      excelData.push({
        Fecha: 'Resumen de ingresos',
        Usuario: 'Ingreso total',
        Correo: '',
        'Tipo de producto': `${summary.totalCount} ventas`,
        'Tipo de membresía': '',
        'Videos extra': '',
        Monto: summary.totalAmount,
        Moneda: currency,
        'Medio de pago': '',
        Referencia: '',
        Región: '',
        Estado: '',
      });

      exportToExcel(
        excelData,
        [
          { wch: 20 },
          { wch: 28 },
          { wch: 32 },
          { wch: 18 },
          { wch: 22 },
          { wch: 28 },
          { wch: 14 },
          { wch: 10 },
          { wch: 24 },
          { wch: 36 },
          { wch: 18 },
          { wch: 18 },
        ],
        'Reporte de Ventas',
        'reporte_ventas',
      );
      message.success('Archivo Excel exportado correctamente.');
    } catch {
      message.error('Error al exportar a Excel.');
    } finally {
      setExporting(false);
    }
  };

  const stateOptions = (regionId ? statesInRegion : states).map((state) => ({
    value: state.id,
    label: state.description,
  }));
  const currency = summaryCurrency(data);

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'purchasedAt',
      key: 'purchasedAt',
      render: (value) => formatDate(value),
      sorter: (a, b) =>
        new Date(a.purchasedAt).getTime() - new Date(b.purchasedAt).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Usuario',
      key: 'user',
      render: (_, item) => (
        <div>
          <div>{item.userName || '-'}</div>
          <div style={{ color: '#888' }}>{item.userEmail}</div>
        </div>
      ),
      sorter: (a, b) => (a.userName || '').localeCompare(b.userName || ''),
    },
    {
      title: 'Tipo de producto',
      dataIndex: 'productType',
      key: 'productType',
      sorter: (a, b) =>
        (a.productType || '').localeCompare(b.productType || ''),
    },
    {
      title: 'Tipo de membresía',
      dataIndex: 'membershipType',
      key: 'membershipType',
      render: (value) => value || '-',
    },
    {
      title: 'Videos extra',
      key: 'addon',
      render: (_, item) => addonDetail(item) || '-',
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      key: 'amount',
      render: (value, item) => formatAmount(value, item.currency),
      sorter: (a, b) => (a.amount ?? 0) - (b.amount ?? 0),
    },
    {
      title: 'Medio de pago',
      dataIndex: 'paymentMedium',
      key: 'paymentMedium',
      render: (value) => mediumLabel(value) || '-',
    },
    {
      title: 'Referencia',
      dataIndex: 'paymentReference',
      key: 'paymentReference',
      render: (value) => value || '-',
    },
    {
      title: 'Región',
      dataIndex: 'region',
      key: 'region',
      render: (value) => value || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'state',
      key: 'state',
      render: (value) => value || '-',
    },
  ];

  return (
    <Card
      title="Reportes - Ventas"
      style={{ margin: 20 }}
      extra={
        <Space>
          <Button
            icon={<SearchOutlined />}
            type="primary"
            onClick={() => handleSearch()}
            loading={loading}
          >
            Buscar
          </Button>
          <Button icon={<ClearOutlined />} onClick={handleClear}>
            Limpiar
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            loading={exporting}
            disabled={!data.length}
          >
            Exportar Excel
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ period: 'month' }}
        onFinish={handleSearch}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Form.Item name="regionId" label="Región">
              <Select
                allowClear
                placeholder="Región"
                options={regions.map((region) => ({
                  value: region.id,
                  label: region.description,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Form.Item name="stateId" label="Estado">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Estado"
                loading={loadingStates}
                options={stateOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Form.Item name="period" label="Periodo">
              <Select options={PERIODS} />
            </Form.Item>
          </Col>
          {period === 'custom' ? (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="dateRange" label="Rango de fechas">
                <RangePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder={['Desde', 'Hasta']}
                />
              </Form.Item>
            </Col>
          ) : null}
        </Row>
      </Form>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Ingreso total"
              value={formatAmount(summary.totalAmount, currency)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={`Membresías (${summary.membershipCount})`}
              value={formatAmount(summary.membershipAmount, currency)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={`Videos extra (${summary.addonCount})`}
              value={formatAmount(summary.addonAmount, currency)}
            />
          </Card>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} registros`,
          }}
          scroll={{ x: 'max-content' }}
          summary={() =>
            data.length > 0 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <strong>Ingreso total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5}>
                  <strong>{formatAmount(summary.totalAmount, currency)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} colSpan={4} />
              </Table.Summary.Row>
            ) : null
          }
        />
      </Spin>
    </Card>
  );
};

export default SalesReport;
